import React, { useRef } from 'react';
import { styled } from '@mui/material';

import { NoMilestones } from 'components/organisms/Milestones';
import {
    Button,
    Box,
    Card,
    Checkbox,
    DatePicker,
    Divider,
    Dropdown,
    Icon,
    IconButton,
    SplitButton,
    Stack,
    Text,
    TextInput,
} from 'shell/components';
import { getDueDate, getMilestoneStatus } from 'utils/helper';
import moment from 'moment';

const Mobile = ({
    filterDrawer,
    numFilters,
    setFilterDrawer,
    calendarOpen,
    setCalendarOpen,
    milestones,
    checked,
    onChangeCheck,
    onChangeCheckAll,
    getCompletion,
    onClickMilestone,
    onClickCreate,
    onClickPublishMulti,
    onClickDeleteMulti,
    onClickViewSubmissions,
    duplicateMilestone,
    deleteMilestone,
    handleMultiDuedate,
    setConfirmationModal,
    closeConfirmationModal,
}) => {
    const anchorRef = useRef(null);
    const handleClickSplitButton = ({ key }) => {
        if (key === 'publish') onClickPublishMulti();
        if (key === 'delete') onClickDeleteMulti();
    };

    const handleSelect = (value, milestoneId) => {
        switch (value) {
            case 'edit':
                return setTimeout(() => onClickMilestone(milestoneId));
            case 'duplicate':
                return duplicateMilestone(milestoneId);
            case 'delete':
                return setConfirmationModal((current) => ({
                    ...current,
                    visible: true,
                    destructive: true,
                    title: 'Delete Milestone and Tasks Associated With This Milestone?',
                    content:
                        'This action cannot be undone. To delete the milestone only, unlink the tasks associated from the milestone.',
                    confirmText: 'Delete',
                    onConfirm: () => {
                        deleteMilestone(milestoneId);
                        closeConfirmationModal();
                    },
                    onCancel: closeConfirmationModal,
                }));
        }
    };

    return (
        <CustomStack numMilestones={milestones.length} spacing={4}>
            <Stack direction={'row'} justifyContent={'space-between'} spacing={2}>
                <Button
                    color={'neutral'}
                    startIcon={<Icon kind={'filter'} />}
                    variant={'tertiary'}
                    onClick={() => setFilterDrawer((current) => ({ ...current, isOpen: true }))}
                >
                    {`Filters ${numFilters ? `(${numFilters})` : ''}`}
                </Button>
                <CustomTextInput
                    disablePointerEventsOnIcon={!Boolean(filterDrawer.searchInput)}
                    endIcon={
                        filterDrawer.searchInput ? (
                            <IconButton
                                aria-label={'clear search input'}
                                kind={'exit'}
                                variant={'square'}
                                onClick={() => setFilterDrawer((current) => ({ ...current, searchInput: '' }))}
                                onMouseDown={(e) => e.preventDefault()}
                            />
                        ) : (
                            <Icon kind={'search'} />
                        )
                    }
                    placeholder={'Search'}
                    value={filterDrawer.searchInput}
                    onChange={(e) => setFilterDrawer((current) => ({ ...current, searchInput: e.target.value }))}
                />
            </Stack>
            <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                <Text variant={'p1'}>Name</Text>
                {checked.length ? (
                    <Stack direction={'row'} spacing={4}>
                        <CustomBox>
                            <Button ref={anchorRef} onClick={() => setCalendarOpen(true)} variant={'tertiary'}>
                                Change Due Date(s)
                            </Button>
                            <DatePicker
                                ref={anchorRef}
                                disabledDate={(date) => date.isSameOrBefore(moment().subtract(1, 'day'))}
                                calendarOnly={true}
                                calendarOpen={calendarOpen}
                                value={moment()}
                                onChange={(date) => handleMultiDuedate(date)}
                            />
                        </CustomBox>
                        <SplitButton
                            options={[
                                { key: 'publish', value: 'Publish' },
                                { key: 'delete', value: 'Delete' },
                            ]}
                            onClick={handleClickSplitButton}
                        />
                    </Stack>
                ) : (
                    <IconButton
                        data-cy={'create-milestone-button'}
                        aria-label={'create new milestone'}
                        kind={'add'}
                        size={'small'}
                        variant={'circle'}
                        onClick={onClickCreate}
                    />
                )}
            </Stack>
            {milestones.length ? (
                milestones.map((milestone) => (
                    <CustomCard key={milestone.id} onClick={() => onClickMilestone(milestone.id)}>
                        <Stack spacing={2}>
                            <Stack divider={<Divider />} spacing={2}>
                                <Stack
                                    alignItems={'flex-start'}
                                    direction={'row'}
                                    justifyContent={'space-between'}
                                    spacing={2}
                                >
                                    <Checkbox
                                        sx={{ m: -2 }}
                                        checked={checked.includes(milestone.id)}
                                        onChange={() => onChangeCheck(milestone.id)}
                                        onClick={(e) => e.stopPropagation()}
                                    />
                                    <Text
                                        data-cy={milestone.title}
                                        sx={{ flex: 1 }}
                                        align={'left'}
                                        variant={'sh2'}
                                        weight={'semibold'}
                                    >
                                        {milestone.title}
                                    </Text>
                                    <Dropdown
                                        id={`${milestone.id}-dropdown`}
                                        sx={{ alignSelf: 'center' }}
                                        options={[
                                            { key: 'edit', value: 'Edit' },
                                            { key: 'duplicate', value: 'Duplicate' },
                                            { key: 'delete', value: 'Delete' },
                                        ]}
                                        rootComponent={
                                            <IconButton
                                                aria-label={'more options'}
                                                kind={'moreHorizontal'}
                                                size={'medium'}
                                                variant={'square'}
                                                onClick={(e) => e.stopPropagation()}
                                            />
                                        }
                                        onSelect={({ key }) => handleSelect(key, milestone.id)}
                                    />
                                </Stack>
                                <Stack spacing={2}>
                                    <Text color={'light'} variant={'p2'}>
                                        {getMilestoneStatus(milestone)}
                                    </Text>
                                    <Text color={'light'} variant={'p2'}>
                                        Due: {getDueDate(milestone)}
                                    </Text>
                                </Stack>
                            </Stack>
                            <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                                <Text color={'light'} variant={'p2'}>
                                    Submissions: {getCompletion(milestone)}
                                </Text>
                                <Button
                                    disabled={getCompletion(milestone) === '--'}
                                    variant={'borderless'}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onClickViewSubmissions(milestone.id);
                                    }}
                                >
                                    View Submissions
                                </Button>
                            </Stack>
                        </Stack>
                    </CustomCard>
                ))
            ) : (
                <NoMilestones />
            )}
        </CustomStack>
    );
};

const CustomStack = styled(Stack)(({ numMilestones, theme }) => {
    return {
        padding: theme.spacing(4, 0, 8),
        ...(!numMilestones && {
            paddingBottom: 0,
        }),
    };
});

const CustomTextInput = styled(TextInput)(() => {
    return {
        flex: 1,
    };
});

const CustomCard = styled(Card)(({ theme }) => {
    return {
        '& > .MuiCardActionArea-root': {
            padding: theme.spacing(3),
        },
    };
});

const CustomBox = styled(Box)(() => {
    return {
        position: 'relative',
    };
});

export default Mobile;
